import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ACMEErrorResponse, extractACMEErrorData, extractACMEErrorMessage } from '@b2b/types/core';

import { AuthenticationService } from '../../core/auth/authentication.service';
import { RedirectService } from '../../core/navigation/redirect.service';
import { RouteParamsService } from '../../core/navigation/route-params.service';
import { ZendeskSsoService, ZendeskSsoSession } from './zendesk-sso.service';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

// AC1 (DEV-11863): Java's error `type` for "authenticated, but no Zendesk
// access". Distinguishes the redirect-to-error case from bad credentials (AC4).
const ZENDESK_UNAUTHORIZED_TYPE = 'UNAUTHORIZED_CALL';

/** Java's SSO error `errorData`, extended with the server-validated returnTo. */
interface ZendeskErrorData extends ACMEErrorResponse {
  returnTo?: string;
}

/**
 * Flow state for the Zendesk SSO login page.
 *  - `checking`   — optimistic seamless attempt in flight (AC3).
 *  - `form`       — show the credential form (no seamless session).
 *  - `submitting` — credential submission in flight.
 */
export type ZendeskLoginMode = 'checking' | 'form' | 'submitting';

/**
 * Zendesk SSO login page (DEV-11861).
 *
 * On load it optimistically attempts a session using the existing b2b cookie;
 * if Rails returns a redirect URL the browser is sent straight to Zendesk
 * (seamless SSO, AC3). Otherwise it shows a reduced credential form — modelled
 * on the standard login page but without the forgot-password link, the SSO
 * button, or the LDAP/tenant branching — and submits the credentials to the
 * same SSO session endpoint (AC2).
 *
 * Field values use Reactive Forms; flow state is held in a signal; the final
 * redirect goes through the injectable {@link RedirectService} seam.
 */
@Component({
  selector: 'app-zendesk-login',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="zendesk-login">
      @if (mode() === 'checking') {
        <div class="zendesk-login-form col-sm-5">
          <div class="zendesk-login-panel panel panel-default panel-inverted">
            <h3 class="zendesk-login-panel-header">One moment please&hellip;</h3>
          </div>
        </div>
      } @else {
        <form
          role="form"
          class="zendesk-login-form col-sm-5"
          [formGroup]="form"
          (ngSubmit)="submit()"
        >
          <div class="zendesk-login-panel panel panel-default panel-inverted">
            <h3 class="zendesk-login-panel-header">
              @if (mode() === 'submitting') {
                <span>One moment please&hellip;</span>
              } @else {
                <span>Sign into your account</span>
              }
            </h3>

            <div class="zendesk-login-panel-body panel-body">
              @if (errorMessage()) {
                <div class="alert alert-danger" role="alert">{{ errorMessage() }}</div>
              }
              <div class="form-group">
                <input
                  type="email"
                  name="email"
                  class="form-control"
                  formControlName="email"
                  placeholder="Email Address"
                  aria-label="Email Address"
                />
              </div>
              <div class="form-group">
                <input
                  type="password"
                  name="password"
                  class="form-control"
                  formControlName="password"
                  placeholder="Password"
                  aria-label="Password"
                />
              </div>
            </div>

            <div class="panel-footer">
              <button
                type="submit"
                class="btn btn-default btn-primary zendesk-login-signin"
                [disabled]="!canSubmit()"
              >
                Sign In
              </button>
            </div>
          </div>
        </form>
      }
    </div>
  `,
})
export class ZendeskLoginComponent implements OnInit {
  private readonly sso = inject(ZendeskSsoService);
  private readonly redirect = inject(RedirectService);
  private readonly auth = inject(AuthenticationService);
  private readonly routeParams = inject(RouteParamsService);

  readonly mode = signal<ZendeskLoginMode>('checking');
  readonly errorMessage = signal<string | null>(null);

  readonly form = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  ngOnInit(): void {
    void this.loadSession();
  }

  /** Submit is allowed only with a valid form and no request already in flight. */
  canSubmit(): boolean {
    return this.form.valid && this.mode() !== 'submitting';
  }

  /**
   * AC3: optimistically attempt a Zendesk session using the existing session
   * cookie. Success → redirect to the client-assembled URL. An UNAUTHORIZED_CALL
   * error, or a success with no JWT, means the account is not authorized for
   * Zendesk → redirect to the error page built from Java's validated returnTo
   * (DEV-11863). Any other failure (e.g. no valid cookie) → credential form.
   */
  async loadSession(): Promise<void> {
    try {
      // The SSO session POST is CSRF-protected; the new Angular CsrfInterceptor
      // only attaches the token once it has been loaded, which nothing does on
      // this landing route. Fetch it before the seamless attempt or Rails
      // rejects the request with 422 (InvalidAuthenticityToken).
      await this.auth.updateCSRF();
      const response = await this.sso.createSession(this.routeParams.get('return_to'));
      const url = this.buildRedirectUrl(response.data?.session);
      if (url) {
        this.redirect.to(url);
        return;
      }
      // AC1/AC2: authenticated via cookie but no JWT issued → not authorized.
      // Use the session's validated returnTo for the Zendesk error page.
      const errorUrl = this.errorUrlFor(response.data?.session?.returnTo);
      if (errorUrl) {
        this.redirect.to(errorUrl);
        return;
      }
      this.mode.set('form');
    } catch (error) {
      // AC1/AC2: UNAUTHORIZED_CALL → this account has no Zendesk access; the
      // credential form would be pointless for the same identity, so go to the
      // error page (built from Java's validated returnTo on the error body).
      const errorData = extractACMEErrorData<ZendeskErrorData>((error as { data?: unknown } | null)?.data);
      if (errorData?.type === ZENDESK_UNAUTHORIZED_TYPE) {
        const errorUrl = this.errorUrlFor(errorData.returnTo);
        if (errorUrl) {
          this.redirect.to(errorUrl);
          return;
        }
      }
      this.mode.set('form');
    }
  }

  /**
   * AC2: submit the entered credentials to the SSO session endpoint and, on
   * success, redirect to the client-assembled Zendesk URL.
   */
  async submit(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }

    this.errorMessage.set(null);
    this.mode.set('submitting');
    try {
      // Ensure the CSRF token is loaded before the (mutating) submit POST, in
      // case the seamless attempt's fetch did not run or failed. See loadSession.
      await this.auth.updateCSRF();
      const response = await this.sso.createSession(
        this.routeParams.get('return_to'),
        this.form.getRawValue(),
      );
      const url = this.buildRedirectUrl(response.data?.session);
      if (url) {
        this.redirect.to(url);
        return;
      }
      // AC1/AC2: a success with no jwt means no Zendesk access was granted;
      // route to the error page using the session's validated returnTo.
      const errorUrl = this.errorUrlFor(response.data?.session?.returnTo);
      if (errorUrl) {
        this.redirect.to(errorUrl);
        return;
      }
      this.errorMessage.set(GENERIC_ERROR);
      this.mode.set('form');
    } catch (error) {
      const body = (error as { data?: unknown } | null)?.data;
      // AC1/AC2: UNAUTHORIZED_CALL → account lacks Zendesk access → error page,
      // built from Java's validated returnTo on the error body.
      const errorData = extractACMEErrorData<ZendeskErrorData>(body);
      if (errorData?.type === ZENDESK_UNAUTHORIZED_TYPE) {
        const errorUrl = this.errorUrlFor(errorData.returnTo);
        if (errorUrl) {
          this.redirect.to(errorUrl);
          return;
        }
      }
      // AC4: bad credentials and other failures stay on the form. Rails owns
      // the customer-facing copy in the standard wrapped ACME error shape; fall
      // back to a generic line if absent.
      this.errorMessage.set(extractACMEErrorMessage(body) || GENERIC_ERROR);
      this.mode.set('form');
    }
  }

  /**
   * Assemble the Zendesk access URL from the session in the Rails envelope.
   * Returns `null` when either field is missing/empty so callers can treat it
   * as a failure instead of navigating to a malformed URL.
   */
  private buildRedirectUrl(session: Partial<ZendeskSsoSession> | undefined): string | null {
    const returnTo = session?.returnTo?.trim();
    const jwt = session?.jwt?.trim();
    if (!returnTo || !jwt) {
      return null;
    }
    return `${returnTo}/access/jwt?jwt=${jwt}`;
  }

  /**
   * AC2 (DEV-11863): the Zendesk error-page URL is the server-validated
   * `returnTo` (Java's success body or forwarded error body) used verbatim —
   * Java owns the full redirect URL — or null when none is present. No host
   * guard is needed: returnTo originates from Java (validated against its
   * allowlist), not from client input.
   */
  private errorUrlFor(returnTo: string | null | undefined): string | null {
    const targetUrl = returnTo?.trim();
    return targetUrl ? targetUrl : null;
  }
}
